import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import moment from "moment";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const AdmissionCycleConfigList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (school_id) {
      fetchAdmissionCycleList();
    }
  }, [school_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  const fetchAdmissionCycleList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: apiSearch || "",
      };

      const response = await AxiosInstance.post(
        "/admission/listAdmissionCycle",
        payload,
      );

      if (response?.data?.status) {
        const cycleData = response.data.data || [];
        const transformedData = cycleData.map((item, index) => ({
          id: item.id,
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          name: item.name || "",
          admission_type: item.admission_type || "",
          start_date: item.start_date
            ? moment(item.start_date).format("DD-MM-YYYY")
            : "",
          end_date: item.end_date
            ? moment(item.end_date).format("DD-MM-YYYY")
            : "",
        }));
        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching admission cycle list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/user/Admission-Cycle-Config-Create");
  };

  const handleView = (row) => {
    navigate(`/user/Admission-Cycle-Config-View`, {
      state: { id: row.id, data: row },
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "sno",
        header: translations?.labels?.serialNo || "S.No",
        size: 80,
        Cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {row.index + 1 + pagination.pageIndex * pagination.pageSize}
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "name",
        header: translations?.labels?.admissionName || "Admission Name",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "admission_type",
        header: translations?.labels?.type || "Type",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "start_date",
        header: translations?.labels?.startDate || "Start Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "end_date",
        header: translations?.labels?.endDate || "End Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "actions",
        header: translations?.labels?.actions || "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div style={{ textAlign: "center" }}>
            <button
              className={styles.viewBtn}
              onClick={() => handleView(row.original)}
              title={translations?.labels?.view || "View"}
            >
              <FaEye />
            </button>
          </div>
        ),
      },
    ],
    [translations, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {translations?.labels?.admissionCycleConfig ||
            "Admission Cycle Configuration"}
        </h1>
      </div>

      {/* Action Button */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-12 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                {translations?.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        placeholder={translations?.labels?.searchName || "Search ..."}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={true}
        enableFiltering={false}
        noRecordsMessage={
          translations.labels?.noRecordsToDisplay || "No records to display"
        }
      />
    </div>
  );
};

export default AdmissionCycleConfigList;
